import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react'; // Import ChakraProvider
import { ColorModeScript } from '@chakra-ui/react';
import SignupPage from './pages/SignupPage';
import LoginPage from './pages/LoginPage';
import MapSearchPage2 from './pages/MapSearchPage2';
import Bookings from './pages/Bookings';
import BookingDetail from './pages/BookingDetail';
import PrivateRoute from './components/PrivateRoute';
import WithSubnavigation from './components/NavBar1';
import UserPage from './pages/UserPage';
import CancelPage from './pages/CancelPage'; // Import the Cancel page
import LandingPage from './pages/LandingPage';
import '../src/components/User/index.css'; // This is custom CSS as well, keep it if needed
import NotificationComponent from './components/Notification';
import TermsPrivacyRefund, { TermsPage } from './pages/Terms';
import OnboardingPage from './pages/OnboardingFlow';
import { CartProvider } from './context/CartContext'; // adjust path as needed
import { Analytics } from "@vercel/analytics/react"
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AffiliateDashboard from './pages/AffiliateDashboard'; 


const App = () => {
  const [photographers, setPhotographers] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const checkLoginStatus = () => {
      setIsLoggedIn(!!localStorage.getItem('token'));
    };
  
    // Initial check
    checkLoginStatus();
  
    // Listen for token updates from other tabs or refreshes
    window.addEventListener('storage', checkLoginStatus);
  
    // Optional: interval to catch edge cases
    const interval = setInterval(checkLoginStatus, 1000);
  
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      clearInterval(interval);
    };
  }, []);
  


  // There was an issue with the map not loading.
  // Looks like it was due to old sessions/cookies in the browser.
  // PWA Probs lol
  useEffect(() => {
    const clearStorageOnClose = () => {
      // Check if sessionStorage has a specific key to detect a refresh
      const isReloading = sessionStorage.getItem('is_reloading');

      if (!isReloading) {
        // Clear storage if it's not a page refresh
        localStorage.clear();
        sessionStorage.clear();
      } else {
        // Remove the flag after a successful reload
        sessionStorage.removeItem('is_reloading');
      }
    };

    const setReloadingFlag = () => {
      // Set a flag to detect page reload
      sessionStorage.setItem('is_reloading', 'true');
    };

    // Listen for the beforeunload event
    window.addEventListener('beforeunload', setReloadingFlag);
    //window.addEventListener('unload', clearStorageOnClose);

    // Cleanup the event listeners
    return () => {
      window.removeEventListener('beforeunload', setReloadingFlag);
      //window.removeEventListener('unload', clearStorageOnClose);
    };
  }, []);


  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('account_id');
      // Clear local storage
    localStorage.clear();

    // Clear session storage
    sessionStorage.clear();
    setIsLoggedIn(false);

    // Unregister the service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister().then((success) => {
            if (success) {
              console.log('Service Worker unregistered successfully.');
            } else {
              console.error('Service Worker unregistration failed.');
            }
          });
        });
      }).catch((error) => {
        console.error('Error during service worker unregistration:', error);
      });
    }
  };

  // Create theme and configure color mode
  const config = {
    initialColorMode: 'dark',  // Set default mode (can be 'light' or 'dark')
    useSystemColorMode: true,  // If true, color mode will follow the user's system preference
  };

  const theme = extendTheme({
    config,
    styles: {
      global: (props) => ({
        body: {
          bg: props.colorMode === 'dark' ? 'black' : 'white',
          color: props.colorMode === 'dark' ? 'white' : 'gray.800',
        },
      }),
    },
  });
  


  return (
    <ChakraProvider theme={theme}> {/* Wrapping the app with ChakraProvider */}
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <CartProvider>
        <Router>
          {/*<NavigationBar isLoggedIn={isLoggedIn} handleLogout={handleLogout} /> {/* Use the Chakra Navbar */}
          <WithSubnavigation isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn}/>
          <Routes>
          <Route path="/" element={<LandingPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage setIsLoggedIn={setIsLoggedIn} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/search"
              element={
                <>
                  {isLoggedIn && <NotificationComponent userId={localStorage.getItem("account_id")} />}
                  <MapSearchPage2 photographers={photographers} setPhotographers={setPhotographers} />
                </>
              }
            />
            <Route path="/booking/:bookingId" element={<BookingDetail />} />
            <Route
              path="/bookings"
              element={
                <PrivateRoute>
                  <Bookings />
                </PrivateRoute>
              }
            />
            <Route
              path="/affiliate"
              element={
                <PrivateRoute>
                  <AffiliateDashboard />
                </PrivateRoute>
              }
            />
            {/*<Route
              path="/booking/:bookingId"
              element={
                <PrivateRoute>
                  <BookingDetail />
                </PrivateRoute>
              }
              />*
            <Route
              path="/transactions"
              element={
                <PrivateRoute>
                  <Transactions />
                </PrivateRoute>
              }
            />/}
            {/* This route is for viewing a user's profile, with or without authentication */}
            <Route path="/user/:user_id" element={<UserPage />} />
            {/* This route is for viewing the logged-in user's own profile */}
            <Route
              path="/user"
              element={
                <PrivateRoute>
                  <UserPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <OnboardingPage setIsLoggedIn={setIsLoggedIn}/> 
              }
              />
            <Route path="/terms" element={<TermsPrivacyRefund />} /> {/* Add the success route */}
            <Route path="/cancel" element={<CancelPage />} />   {/* Add the cancel route */}
            {/* Optionally, redirect unknown routes to the home page */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </CartProvider>
      <Analytics />
    </ChakraProvider>
  );
};

export default App;
